import React, { useState, useMemo } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  PlusIcon, 
  XIcon, 
  GripVerticalIcon,
  CopyIcon,
  MoreVerticalIcon,
  CheckCircleIcon,
  XCircleIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../ui/tooltip";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import RoutingRuleEditor from "./RoutingRuleEditor";

type RouteTarget = { 
  type: "agent" | "action" | "llm" | "none"; 
  agent_id?: number; 
  action_id?: number; 
};
type RoutingRule = { 
  id?: string; 
  if: string; 
  route: RouteTarget; 
  enabled?: boolean; 
  label?: string;
};

interface SubAgent {
  id: number;
  agent_name: string;
}

interface Action {
  id: number;
  action_name: string;
}

interface FormField {
  name: string;
  description: string;
  type: string;
  readScope: string;
  writeScope: string;
}

interface RoutingRulesTableProps {
  rules: RoutingRule[];
  onRuleAdd: (rule: RoutingRule) => void;
  onRuleUpdate: (index: number, rule: RoutingRule) => void;
  onRuleDelete: (index: number) => void;
  onRuleReorder: (fromIndex: number, toIndex: number) => void;
  subAgents: SubAgent[];
  actions: Action[];
  formFields: FormField[];
  agent: any; // Full agent object with sub_agents tree
  apiKey: ApiKeyOrGetter; // Still needed for RoutingRuleEditor
  isVersionView?: boolean;
}

const RoutingRulesTable: React.FC<RoutingRulesTableProps> = ({
  rules,
  onRuleAdd,
  onRuleUpdate,
  onRuleDelete,
  onRuleReorder,
  subAgents,
  actions,
  formFields,
  agent,
  apiKey,
  isVersionView = false,
}) => {
  const [ruleModalOpen, setRuleModalOpen] = useState<boolean>(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [activeDropZone, setActiveDropZone] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isReordering, setIsReordering] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const handleAddRule = () => {
    setEditingRuleIndex(null);
    setRuleModalOpen(true);
  };

  const handleEditRule = (index: number) => {
    setEditingRuleIndex(index);
    setRuleModalOpen(true);
  };

  const handleRuleSave = (rule: RoutingRule) => {
    if (editingRuleIndex !== null) {
      onRuleUpdate(editingRuleIndex, rule);
    } else {
      onRuleAdd(rule);
    }
    setRuleModalOpen(false);
    setEditingRuleIndex(null);
  };

  const handleRuleCancel = () => {
    setRuleModalOpen(false);
    setEditingRuleIndex(null);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (isVersionView) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    
    // Small delay before enabling drop zones
    setTimeout(() => {
      setIsDragging(true);
    }, 50);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedIndex(null);
    setActiveDropZone(null);
    setIsDragging(false);
  };

  const handleDropZoneEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null) {
      setActiveDropZone(index);
    }
  };

  const handleDropZoneDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isVersionView) return; // Don't allow reordering when viewing a version
    
    if (draggedIndex !== null && draggedIndex !== dropIndex && !isReordering) {
      // Calculate the target index after the item is removed
      const targetIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      
      // Prevent concurrent reorders
      setIsReordering(true);
      
      try {
        onRuleReorder(draggedIndex, targetIndex);
      } finally {
        // Reset reordering flag after a delay
        setTimeout(() => {
          setIsReordering(false);
        }, 500);
      }
    }
    
    setDraggedIndex(null);
    setActiveDropZone(null);
    setIsDragging(false);
  };

  // Render a drop zone that looks like a table row
  const renderDropZone = (index: number): React.ReactNode => {
    const isActive = activeDropZone === index;
    
    if (!isDragging) return null;
    
    return (
      <div
        className={cn(
          "px-6 transition-all duration-200 flex items-center justify-center border-y",
          isActive 
            ? "py-4 bg-maitai-green/10 border-maitai-green" 
            : "py-1 bg-muted/20 border-transparent hover:py-3 hover:bg-maitai-green/5 hover:border-maitai-green/30"
        )}
        onDragEnter={(e) => handleDropZoneEnter(e, index)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDropZoneDrop(e, index)}
      >
        {isActive && (
          <div className="flex items-center gap-2">
            <div className="h-0.5 flex-1 bg-maitai-green/30" />
            <span className="text-xs font-gc-med text-maitai-green px-3 py-1 bg-maitai-green/20 rounded-full">
              Drop here
            </span>
            <div className="h-0.5 flex-1 bg-maitai-green/30" />
          </div>
        )}
      </div>
    );
  };

  // Build flat lookup maps for easy reference
  const agentLookup = useMemo(() => {
    const lookup: Record<number, { name: string; actions: any[] }> = {};
    
    const traverseAgent = (agent: any) => {
      if (agent && agent.id) {
        // Ensure ID is a number for consistent lookup
        const agentId = Number(agent.id);
        lookup[agentId] = {
          name: agent.agent_name || `Agent #${agent.id}`,
          actions: agent.actions || []
        };
        
        // Traverse sub-agents recursively
        if (agent.sub_agents && Array.isArray(agent.sub_agents)) {
          agent.sub_agents.forEach((subAgent: any) => traverseAgent(subAgent));
        }
      }
    };
    
    if (agent) {
      traverseAgent(agent);
    }
    
    return lookup;
  }, [agent]);

  const formatRouteTarget = (route: RouteTarget): { agentName: string; actionName?: string } => {
    switch (route.type) {
      case "agent": {
        const agentId = Number(route.agent_id);
        const agentInfo = agentLookup[agentId];
        const agentName = agentInfo ? agentInfo.name : `Agent #${route.agent_id}`;
        
        if (route.action_id && agentInfo) {
          const actionId = Number(route.action_id);
          const action = agentInfo.actions.find((a: any) => Number(a.id) === actionId);
          const actionName = action ? action.action_name : `Action #${route.action_id}`;
          return { agentName, actionName };
        }
        return { agentName };
      }
      case "action": {
        const actionId = Number(route.action_id);
        const action = actions.find(a => Number(a.id) === actionId);
        return { agentName: action ? action.action_name : `Action #${route.action_id}` };
      }
      case "llm":
        return { agentName: "LLM" };
      case "none":
        return { agentName: "None" };
      default:
        return { agentName: "Unknown" };
    }
  };

  const truncateExpression = (expression: string | undefined, maxLength: number = 60): string => {
    if (!expression) return "";
    if (expression.length <= maxLength) return expression;
    return expression.substring(0, maxLength) + "...";
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="space-y-1">
              <h4 className="text-base font-gc-med text-foreground">Routing Rules</h4>
              <p className="text-sm text-muted-foreground">
                Rules are evaluated in order. First matching rule wins.
              </p>
            </div>
            <Button 
              onClick={handleAddRule}
              size="sm" 
              className="gap-2"
              disabled={isVersionView}
            >
              <PlusIcon className="w-4 h-4" />
              New Rule
            </Button>
          </div>

          {/* Rules list */}
          <div className="divide-y divide-border">
            {rules.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="space-y-4">
                  <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center">
                    <PlusIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-base font-gc-med text-foreground">No routing rules defined</h4>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      Create rules to route requests based on state variables, request content, or semantic matching.
                    </p>
                  </div>
                  <Button onClick={handleAddRule} className="gap-2" disabled={isVersionView}>
                    <PlusIcon className="w-4 h-4" />
                    Add First Rule
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {rules.map((rule, index) => (
                  <React.Fragment key={index}>
                    {/* Drop zone before this rule */}
                    {renderDropZone(index)}
                    
                    {/* Rule row */}
                    <div 
                      onClick={() => {
                        if (!isVersionView) {
                          handleEditRule(index);
                        }
                      }}
                      className={cn(
                        "px-6 py-4 flex items-center gap-3 transition-colors",
                        isVersionView ? "cursor-default" : "hover:bg-accent/50 cursor-pointer",
                        draggedIndex === index && "opacity-50"
                      )}
                    >
                      {/* Drag handle */}
                      <div 
                        className="flex-shrink-0" 
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          draggable={!isVersionView}
                          onDragStart={(e) => {
                            if (isVersionView) {
                              e.preventDefault();
                              return;
                            }
                            e.stopPropagation();
                            handleDragStart(e, index);
                          }}
                          onDragEnd={(e) => {
                            e.stopPropagation();
                            handleDragEnd(e);
                          }}
                          className={cn(
                            "select-none flex items-center px-1.5 py-1 hover:bg-muted/70 rounded transition-colors",
                            isVersionView ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing"
                          )}
                          style={{ userSelect: "none", WebkitUserSelect: "none", touchAction: "none" }}
                          title={isVersionView ? "Cannot reorder rules when viewing a version" : "Drag to reorder"}
                        >
                          <GripVerticalIcon className="w-5 h-5 text-muted-foreground shrink-0" />
                        </div>
                      </div>

                  {/* Rule number and status */}
                  <div className="flex-shrink-0 w-8 text-center flex flex-col items-center gap-1">
                    <span className="text-xs font-gc-med text-muted-foreground">
                      {index + 1}
                    </span>
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      rule.enabled !== false ? "bg-green-500" : "bg-red-500"
                    )} />
                  </div>

                  {/* Condition */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-gc-med text-foreground">
                      {rule.label?.trim() || "Unnamed rule"}
                    </div>
                    <Tooltip content={rule.if}>
                      <div className="text-sm font-mono text-muted-foreground cursor-help">
                        {truncateExpression(rule.if)}
                      </div>
                    </Tooltip>
                  </div>

                  {/* Route target */}
                  <div className="flex-shrink-0">
                    <div className="text-sm font-gc-med text-maitai-green bg-maitai-green/10 px-2 py-1 rounded text-center">
                      {(() => {
                        const { agentName, actionName } = formatRouteTarget(rule.route);
                        if (actionName) {
                          return (
                            <div className="flex flex-col items-center">
                              <div>{agentName}</div>
                              <div className="text-xs">↓</div>
                              <div className="text-xs">{actionName}</div>
                            </div>
                          );
                        }
                        return agentName;
                      })()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVerticalIcon className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isVersionView) {
                            onRuleUpdate(index, { ...rule, enabled: !rule.enabled });
                            }
                          }}
                          disabled={isVersionView}
                        >
                          {rule.enabled !== false ? (
                            <>
                              <XCircleIcon className="w-4 h-4 mr-2" />
                              Disable
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="w-4 h-4 mr-2" />
                              Enable
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isVersionView) {
                            onRuleAdd({ ...rule, id: `rule_${Date.now()}` });
                            }
                          }}
                          disabled={isVersionView}
                        >
                          <CopyIcon className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isVersionView) {
                              setPendingDeleteIndex(index);
                              setDeleteDialogOpen(true);
                            }
                          }}
                          disabled={isVersionView}
                          className="text-red-600 focus:text-red-600"
                        >
                          <XIcon className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                  </React.Fragment>
                ))}
                
                {/* Drop zone after all rules */}
                {renderDropZone(rules.length)}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rule editor modal */}
      <RoutingRuleEditor
        isOpen={ruleModalOpen}
        onClose={handleRuleCancel}
        onSave={handleRuleSave}
        editingRule={editingRuleIndex !== null ? rules[editingRuleIndex] : null}
        subAgents={subAgents}
        actions={actions}
        formFields={formFields}
        agent={agent}
        apiKey={apiKey}
      />

      {/* Delete Rule Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The routing rule will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDeleteIndex(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteIndex !== null) {
                  onRuleDelete(pendingDeleteIndex);
                }
                setDeleteDialogOpen(false);
                setPendingDeleteIndex(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RoutingRulesTable;
